import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import RoutineTemplateViewer from '../routines/RoutineTemplateViewer';
import RoutineTemplateEditor from '../routines/RoutineTemplateEditor';
import { emptyLiveClassRoutine, routineFromApi } from '../../shared/routineTemplateConstants';
import { ROUTINE_CLASS_EDITOR_MODAL_CSS } from './routineClassEditorModalStyles';

function snapshotFromDraft(draft, routineId, routineMeta) {
  return {
    id: Number(routineId),
    nombre: draft.nombre,
    categoria: draft.categoria,
    subcategoria: draft.subcategoria,
    objetivo: draft.objetivo,
    nivel: draft.nivel,
    duracion: draft.duracion,
    descripcion: draft.descripcion,
    notas_tecnicas: draft.notas_tecnicas,
    equipamiento: draft.equipamiento || [],
    version: routineMeta?.version ?? null,
    blocks: draft.blocks || [],
  };
}

export default function LiveClassRoutineHost({
  classItem,
  user,
  gymScoped = false,
  editable = false,
  onRoutineUpdated = null,
}) {
  const routine = classItem?.d28d_routine_snapshot || classItem?.d28d_routine;
  const sessionAdjustments = routine?.session_adjustments
    || classItem?.d28d_session_adjustments
    || '';
  const [note, setNote] = useState('');
  const [notes, setNotes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyLiveClassRoutine({ scope: gymScoped ? 'gym_wl' : 'd28d_platform' }));
  const [sessionAdjDraft, setSessionAdjDraft] = useState(sessionAdjustments);
  const [routineSaving, setRoutineSaving] = useState(false);
  const [routineMsg, setRoutineMsg] = useState('');
  const [routineErr, setRoutineErr] = useState('');

  const categoryOptions = useMemo(() => {
    const names = [];
    if (routine?.categoria) names.push(routine.categoria);
    if (editForm.categoria && !names.includes(editForm.categoria)) names.push(editForm.categoria);
    return names.length ? names : ['Full Body'];
  }, [routine?.categoria, editForm.categoria]);

  const loadNotes = async () => {
    if (!routine?.id && !classItem?.d28d_routine_id) return;
    try {
      const res = await api.get('/d28d/routines/notes/host', {
        params: {
          live_class_id: classItem?.id,
          routine_id: routine?.id || classItem?.d28d_routine_id,
        },
      });
      setNotes(res.data?.data || []);
    } catch {
      setNotes([]);
    }
  };

  useEffect(() => {
    loadNotes();
  }, [classItem?.id, routine?.id]);

  useEffect(() => {
    setSessionAdjDraft(sessionAdjustments);
  }, [classItem?.id, sessionAdjustments]);

  const openEditor = () => {
    if (!routine) return;
    setEditForm(routineFromApi(routine));
    setSessionAdjDraft(sessionAdjustments);
    setRoutineMsg('');
    setRoutineErr('');
    setEditorOpen(true);
  };

  const saveRoutineForClass = async () => {
    if (!classItem?.id) return;
    const routineId = routine?.id || classItem?.d28d_routine_id;
    if (!routineId) return;
    setRoutineSaving(true);
    setRoutineErr('');
    setRoutineMsg('');
    try {
      const snapshot = {
        ...snapshotFromDraft(editForm, routineId, routine),
        session_adjustments: (sessionAdjDraft || '').trim() || null,
      };
      await api.put(`/live-classes/admin/${classItem.id}`, {
        d28d_routine_id: Number(routineId),
        d28d_routine_snapshot: snapshot,
        d28d_session_adjustments: snapshot.session_adjustments,
      });
      setRoutineMsg(gymScoped
        ? 'Rutina de la clase actualizada.'
        : 'Rutina D28D de la clase actualizada.');
      setEditorOpen(false);
      onRoutineUpdated?.();
    } catch (err) {
      setRoutineErr(err.response?.data?.error || 'No se pudo guardar la rutina de la clase.');
    } finally {
      setRoutineSaving(false);
    }
  };

  if (!routine) {
    return (
      <p className="text-sm text-slate-500 mt-4">
        {gymScoped
          ? 'Esta clase no tiene rutina asignada. Puedes programarla manualmente o elegir una plantilla al crear/editar la clase.'
          : 'Esta clase no tiene rutina D28D asignada. Puedes programarla manualmente o elegir una plantilla al crear/editar la clase.'}
      </p>
    );
  }

  const saveNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post('/d28d/routines/notes/host', {
        routine_id: routine.id || classItem.d28d_routine_id,
        live_class_id: classItem.id,
        texto: note,
      });
      setNote('');
      await loadNotes();
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mt-6 rounded-2xl border border-slate-200 ui-light-panel d28d-admin-surface surface-readable p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold text-stone-900">
          {gymScoped ? 'Rutina — vista entrenador' : 'Rutina D28D — vista anfitrión'}
        </h3>
        {editable ? (
          <button type="button" className="btn-secondary text-sm" onClick={openEditor}>
            Editar rutina de la clase
          </button>
        ) : null}
      </div>
      <p className="text-xs text-stone-600 mt-2">
        Rutina de esta sesión (copia de clase). Tus observaciones no modifican la plantilla maestra.
      </p>

      {routineMsg ? <p className="text-sm text-emerald-700 mt-2">{routineMsg}</p> : null}
      {routineErr ? <p className="text-sm text-red-700 mt-2">{routineErr}</p> : null}

      <div className="mt-4">
        <RoutineTemplateViewer routine={routine} sessionAdjustments={sessionAdjustments} />
      </div>

      <div className="mt-4 pt-4 border-t border-slate-200">
        <label className="block text-sm font-semibold text-stone-800 mb-1">Observaciones del anfitrión</label>
        <textarea
          className="input w-full min-h-[72px]"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Notas para esta clase (no alteran la plantilla)…"
        />
        <button type="button" className="btn-primary mt-2 text-sm" disabled={saving} onClick={saveNote}>
          {saving ? 'Guardando…' : 'Registrar observación'}
        </button>
        {notes.length > 0 && (
          <ul className="mt-3 text-sm space-y-2">
            {notes.map((n) => (
              <li key={n.id} className="bg-slate-50 rounded-lg p-2 border border-slate-200 text-stone-800">
                <span className="text-xs text-stone-500">{new Date(n.created_at).toLocaleString()}</span>
                <p className="text-stone-800">{n.texto}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editorOpen && typeof document !== 'undefined' ? createPortal(
        <>
          <style>{ROUTINE_CLASS_EDITOR_MODAL_CSS}</style>
          <div className="routine-class-editor-overlay fixed inset-0 z-[1200] flex items-start sm:items-center justify-center bg-black/50 p-3 sm:p-4 overflow-y-auto" role="dialog" aria-modal="true">
            <div
              className="routine-class-editor-modal routine-class-editor-island rounded-2xl shadow-xl max-w-4xl w-full max-h-[min(92dvh,calc(100dvh-1.5rem))] overflow-y-auto p-4 sm:p-5 space-y-4 border border-slate-200 my-auto"
              style={{ backgroundColor: '#ffffff', color: '#0f172a' }}
            >
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-bold" style={{ color: '#0f172a' }}>Editar rutina de esta clase</h4>
                <button type="button" className="btn-secondary text-sm" onClick={() => setEditorOpen(false)}>Cerrar</button>
              </div>
              <RoutineTemplateEditor
                form={editForm}
                setForm={setEditForm}
                categoryOptions={categoryOptions}
                readOnly={false}
                liveClass
              />
              <label className="block text-sm font-semibold" style={{ color: '#0f172a' }}>
                Ajustes de sesión (solo esta clase)
                <textarea
                  className="input w-full min-h-[64px] mt-1"
                  value={sessionAdjDraft}
                  onChange={(e) => setSessionAdjDraft(e.target.value)}
                  placeholder="Cambios puntuales para hoy…"
                />
              </label>
              <div className="flex gap-2 pt-2 border-t border-slate-200">
                <button type="button" className="btn-primary text-sm" disabled={routineSaving} onClick={saveRoutineForClass}>
                  {routineSaving ? 'Guardando…' : 'Guardar rutina'}
                </button>
                <button type="button" className="btn-secondary text-sm" onClick={() => setEditorOpen(false)}>Cancelar</button>
              </div>
            </div>
          </div>
        </>,
        document.body,
      ) : null}
    </section>
  );
}
